import { useEffect, useRef, useState } from "react";
import { ChevronDown, Menu, Monitor, Moon, Sun, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PrimaryButton, SecondaryButton } from "@/components/ui/primitives";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp, type ThemeMode } from "@/context/AppProviders";
import { useSiteContent } from "@/content/site";
import { useServicesNavItems, ServicesMegaMenu, ServicesMobileMenu } from "@/components/layout/ServicesMegaMenu";
import { liquidGlass } from "@/components/ui/LiquidGlass";
import {
  blogPath,
  contactPath,
  hasBlog,
  homePath,
  pricingPath,
  switchLocalePath,
} from "@/lib/routes";
import { EnabaseLogo } from "@/components/brand/EnabaseLogo";
import { cn, LINKS } from "@/lib/utils";
import type { ReactNode } from "react";

const THEME_OPTIONS: { mode: ThemeMode; Icon: typeof Sun; label: string }[] = [
  { mode: "light", Icon: Sun, label: "Light" },
  { mode: "dark", Icon: Moon, label: "Dark" },
  { mode: "system", Icon: Monitor, label: "System" },
];

function HeaderNavLink({
  to,
  children,
  onClick,
  className,
}: {
  to: string;
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        "rounded-lg px-3 py-2 text-sm font-medium text-[var(--nav-text)] no-underline transition-colors",
        "hover:bg-[var(--surface-inset)] hover:text-[var(--text)]",
        className
      )}
    >
      {children}
    </Link>
  );
}

function ThemeToggle({ compact }: { compact?: boolean }) {
  const { themeMode, setThemeMode } = useApp();

  return (
    <div
      className={cn(
        "flex items-center rounded-full border border-[var(--border)] bg-[var(--surface-inset)] p-0.5",
        compact && "w-full justify-between"
      )}
      role="group"
      aria-label="Theme"
    >
      {THEME_OPTIONS.map(({ mode, Icon, label }) => (
        <button
          key={mode}
          type="button"
          title={label}
          onClick={() => setThemeMode(mode)}
          className={cn(
            "flex items-center justify-center rounded-full transition-colors",
            compact ? "size-9 flex-1" : "size-8",
            themeMode === mode
              ? "bg-[var(--card)] text-[var(--text)] shadow-sm"
              : "text-[var(--text-sm)] hover:text-[var(--text)]"
          )}
        >
          <Icon className="size-3.5" strokeWidth={1.75} />
        </button>
      ))}
    </div>
  );
}

function LanguageToggle({ className }: { className?: string }) {
  const { locale, setLocale } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const targetLocale = locale === "tr" ? "en" : "tr";

  return (
    <button
      type="button"
      onClick={() => {
        const nextPath = switchLocalePath(location.pathname + location.hash, targetLocale);
        setLocale(targetLocale);
        navigate(nextPath);
      }}
      className={cn(
        "inline-flex h-9 min-w-9 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface-inset)] px-2.5",
        "text-xs font-semibold tracking-wide text-[var(--text-md)] transition hover:text-[var(--text)]",
        className
      )}
      aria-label={locale === "tr" ? "Switch to English" : "Türkçe'ye geç"}
    >
      {locale === "tr" ? "EN" : "TR"}
    </button>
  );
}

export function SiteHeader() {
  const { locale } = useApp();
  const c = useSiteContent(locale);
  const servicesNav = useServicesNavItems(locale);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const servicesButtonRef = useRef<HTMLButtonElement>(null);
  const servicesMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!servicesOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (servicesRef.current?.contains(target)) return;
      if (servicesMenuRef.current?.contains(target)) return;
      setServicesOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setServicesOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [servicesOpen]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) setMobileServicesOpen(false);
  }, [open]);

  const closeServices = () => setServicesOpen(false);
  const closeMobile = () => setOpen(false);

  const navLinks = [
    { label: c.nav.pricing, href: pricingPath(locale) },
    ...(hasBlog(locale) ? [{ label: c.nav.blog, href: blogPath(locale) }] : []),
    { label: c.nav.contact, href: contactPath(locale) },
  ];

  return (
    <header
      className={cn(
        "liquid-glass-nav fixed inset-x-0 top-0 z-100 overflow-visible border-b border-[var(--nav-b)] transition-all duration-300",
        scrolled && "nav-glass-scrolled"
      )}
    >
      <div className="mx-auto flex h-16 max-w-[var(--layout-max)] items-center gap-4 overflow-visible px-5 sm:gap-6 sm:px-7 lg:px-9">
        <Link to={homePath(locale)} className="flex shrink-0 items-center no-underline" aria-label="Enabase">
          <EnabaseLogo className="h-9 w-auto max-w-[11.5rem]" />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-0.5 overflow-visible lg:flex" aria-label="Ana menü">
          <div ref={servicesRef} className="relative">
            <button
              ref={servicesButtonRef}
              type="button"
              aria-expanded={servicesOpen}
              aria-haspopup="menu"
              onClick={() => setServicesOpen((v) => !v)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                servicesOpen
                  ? "bg-[var(--surface-inset)] text-[var(--text)]"
                  : "text-[var(--nav-text)] hover:bg-[var(--surface-inset)] hover:text-[var(--text)]"
              )}
            >
              {c.nav.services}
              <ChevronDown
                className={cn("size-3.5 opacity-60 transition-transform", servicesOpen && "rotate-180")}
              />
            </button>
          </div>

          <ServicesMegaMenu
            open={servicesOpen}
            menuRef={servicesMenuRef}
            items={servicesNav}
            onClose={closeServices}
          />

          {navLinks.map((item) => (
            <HeaderNavLink key={item.href} to={item.href}>
              {item.label}
            </HeaderNavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex lg:gap-2">
          <ThemeToggle />
          <LanguageToggle />
          <span className="mx-1 h-5 w-px bg-[var(--border)]" aria-hidden />
          <Button variant="ghost" size="sm" asChild className="text-[var(--nav-text)] hover:text-[var(--text)]">
            <a href={LINKS.login} target="_blank" rel="noopener noreferrer">
              {c.nav.login}
            </a>
          </Button>
          <PrimaryButton
            href={LINKS.register}
            external
            className="rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            {c.nav.register}
          </PrimaryButton>
        </div>

        <button
          type="button"
          className={cn(liquidGlass("pill"), "ml-auto inline-flex size-10 items-center justify-center text-[var(--text)] lg:hidden")}
          aria-expanded={open}
          aria-label={open ? "Menüyü kapat" : "Menüyü aç"}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="size-4" /> : <Menu className="size-4" />}
        </button>
      </div>

      {open ? (
        <div className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-[var(--border)] bg-[var(--nav-bg)] lg:hidden">
            <div className="mx-auto max-w-[var(--layout-max)] space-y-1 px-5 py-4 sm:px-7">
              <ServicesMobileMenu
                open={mobileServicesOpen}
                onToggle={() => setMobileServicesOpen((v) => !v)}
                items={servicesNav}
                servicesLabel={c.nav.services}
                onNavigate={closeMobile}
              />

              {navLinks.map((item) => (
                <HeaderNavLink key={item.href} to={item.href} onClick={closeMobile} className="block px-2">
                  {item.label}
                </HeaderNavLink>
              ))}

              <div className="mt-4 space-y-3 border-t border-[var(--border)] pt-4">
                <div className="flex items-center gap-2">
                  <ThemeToggle compact />
                  <LanguageToggle className="h-10 min-w-10" />
                </div>
                <div className="flex gap-2">
                  <SecondaryButton
                    href={LINKS.login}
                    external
                    size="default"
                    className="h-11 min-w-0 flex-1 rounded-full px-3 text-sm font-semibold"
                  >
                    {c.nav.login}
                  </SecondaryButton>
                  <PrimaryButton
                    href={LINKS.register}
                    external
                    size="default"
                    className="h-11 min-w-0 flex-1 rounded-full px-3 text-sm font-semibold"
                  >
                    {c.nav.register}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
      ) : null}
    </header>
  );
}
